/*
  Autosuggest input is responsible for helping the user to choose
  an account or project or similar, to filter the data displayed in charts.

  When the object is valid we make the input text bold. 

  There is a X button to clear the input.

  Component can be disabled.

  User can use arrows to navigate through suggestions.
*/

import React from "react";
import compose from "recompose/compose";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Autosuggest from "react-autosuggest";
import Paper from "material-ui/Paper";
import { grey } from "material-ui/colors";
import { ListItem, ListItemText } from "material-ui/List";
import Typography from "material-ui/Typography";
import IconButton from "material-ui/IconButton";
import CloseIcon from "material-ui-icons/Close";

const AutosuggestInput = props => {
  const {
    classes,
    label,
    disabled,
    suggestions,
    placeholder,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    inputValue,
    inputValid,
    filterKey
  } = props;

  const getSectionSuggestions = section => {
    return section.suggestions;
  };

  const getSuggestionValue = suggestion => {
    props.onChange(filterKey, suggestion);
    return suggestion.text;
  };

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    return (
      <ListItem
        dense
        component="div"
        className={
          isHighlighted
            ? classes.hilightedSuggestion
            : classes.unhilightedSuggestion
        }
      >
        <ListItemText primary={suggestion.text} />
      </ListItem>
    );
  };

  const renderSuggestionsContainer = options => {
    const { containerProps, children } = options;
    return <Paper {...containerProps}>{children}</Paper>;
  };

  const shouldRenderSuggestions = value => {
    return true;
  };

  const clearInput = () => {
    props.onChange(filterKey, "");
  };

  const onChangeInput = (evt, { newValue }) => {
    const obj = suggestions.find(el => el.text === newValue);

    if (obj) {
      props.onChange(filterKey, obj);
    } else {
      props.onChange(filterKey, newValue);
    }
  };

  const renderClearButton = () => {
    if (!inputValue) {
      return null;
    }

    return (
      <IconButton
        onClick={clearInput}
        color="default"
        className={classes.closeButton}
        disabled={inputValue === ""}
      >
        <CloseIcon />
      </IconButton>
    );
  };

  const renderInputComponent = inputProps => (
    <div>
      {renderClearButton()}
      <input {...inputProps} />
    </div>
  );

  const labelClass = `${classes.cellLabel} ${
    disabled ? classes.cellLabelDisabled : ""
  }`;

  return (
    <div className={classes.wrapper}>
      <Typography className={labelClass}>{label}</Typography>
      <div className={classes.flexrow}>
        <Autosuggest
          theme={{
            container: classes.container,
            suggestionsContainerOpen: classes.suggestionsContainerOpen,
            suggestionsContainer: classes.suggestionsContainer,
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion
          }}
          focusInputOnSuggestionClick={false}
          renderSuggestionsContainer={renderSuggestionsContainer}
          suggestions={suggestions}
          shouldRenderSuggestions={shouldRenderSuggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          getSectionSuggestions={getSectionSuggestions}
          renderSuggestion={renderSuggestion}
          renderInputComponent={renderInputComponent}
          inputProps={{
            disabled,
            placeholder,
            value: inputValue,
            onChange: onChangeInput,
            className: `${classes.input} ${
              inputValid ? classes.inputValid : ""
            }`
          }}
        />
      </div>
    </div>
  );
};

AutosuggestInput.propTypes = {
  disabled: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  filterKey: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  inputValid: PropTypes.bool.isRequired,
  inputValue: PropTypes.string,
  suggestions: PropTypes.array,
  onSuggestionsFetchRequested: PropTypes.func.isRequired,
  onSuggestionsClearRequested: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

const styles = theme => ({
  wrapper: {
    paddingTop: 10,
    position: "relative",
    flexShrink: 0,
    paddingLeft: 16,
    paddingBottom: 4,
    height: 54
  },
  cellLabel: {
    fontSize: 13,
    marginBottom: 4
  },
  cellLabelDisabled: {
    color: theme.palette.text.disabled
  },
  input: {
    borderRadius: 2,
    fontSize: 15,
    width: "calc(100% - 28px)",
    flexGrow: 1,
    borderWidth: 1,
    borderColor: theme.palette.common.faintBlack,
    borderStyle: "solid",
    height: 14,
    padding: 7,
    border: 0,
    display: "block",
    verticalAlign: "middle",
    background: "none",
    margin: 0,
    "&:focus": {
      outline: 0
    },
    "&::placeholder": {
      color: "rgba(0,0,0,0.3)"
    },
    color: theme.palette.common.lightBlack
  },

  inputValid: {
    color: theme.palette.common.black,
    fontWeight: 600
  },
  container: {
    flexGrow: 1,
    position: "relative"
  },
  suggestionsContainerOpen: {
    position: "absolute",
    left: 0,
    minWidth: 212,
    zIndex: 6
  },
  suggestionsContainer: {
    maxHeight: 300,
    overflowY: "auto"
  },
  suggestion: {},
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: "none"
  },
  subheader: {
    backgroundColor: grey[100]
  },
  hilightedSuggestion: {
    backgroundColor: theme.palette.common.faintBlack
  },
  unhilightedSuggestion: {
    backgroundColor: theme.palette.common.white
  },
  flexrow: {
    display: "flex",
    flexDirection: "row"
  },
  closeButton: {
    position: "absolute",
    right: 0,
    top: -8
  }
});

export { AutosuggestInput };

export default compose(withStyles(styles, { name: "AutosuggestInput" }))(
  AutosuggestInput
);



// WEBPACK FOOTER //
// ./src/AutosuggestInput.js